import { Add, Delete, Edit, Search } from "@mui/icons-material";
import {
    Box, Breadcrumbs,
    Button,
    IconButton, MenuItem,
    Paper, Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, TextField,
    Typography, useMediaQuery, useTheme
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers";
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import api from '../../api';
import { useListFetch } from '../../hooks/useListFetch';
import DidNotLoad from "../../components/DidNotLoad";
import { ColorSwatchLabel } from '../../components/HtmlColorField';
import Loading from '../../components/Loading';
import CustomPagination from "../../components/Pagination";
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatCurrencyInCents, formatDate, formatUuid } from '../../helpers/formatters';

function ListMaterialsPage() {
    const [materials, setMaterials] = useState([]);
    const [isLoading, setIsLoading] = useState(true); // Estado para controlar o carregamento
    const navigate = useNavigate(); // Usando useNavigate para redirecionamento
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1); // Página atual
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(false);
    const [activeSearchQuery, setActiveSearchQuery] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchMaterials = useCallback(() => {
        setIsLoading(true); // Indica que o carregamento está em andamento
        api.get('filaments', {
            params: {
                page: currentPage,
                limit: itemsPerPage,
                q: activeSearchQuery,
            },
        })
            .then(response => {
                setMaterials(response.data.materials);
                setTotalPages(response.data.total_pages);
            })
            .catch(error => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false); // Finaliza o carregamento
            });
    }, [currentPage, itemsPerPage, activeSearchQuery]);

    useListFetch(fetchMaterials, [currentPage, itemsPerPage, activeSearchQuery, fetchMaterials]);

    const handleSearch = () => {
        const nextQuery = searchTerm.trim();
        setCurrentPage(1);
        setActiveSearchQuery(nextQuery);
        setAppliedSearchTerm(Boolean(nextQuery));
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setActiveSearchQuery('');
        setCurrentPage(1);
        setAppliedSearchTerm(false);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleCreateMaterial = () => {
        navigate('/admin/register-material');  // Redireciona para a página de criação de material
    };

    const handleEditMaterial = (id) => {
        navigate(`/admin/edit-material/${id}`);  // Redireciona para a página de edição
    };

    const handleDeleteMaterial = (materialId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Este material será removido permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete, // Impede que o SweetAlert seja fechado durante o carregamento
            preConfirm: () => {
                if (!loadingDelete) {
                    setLoadingDelete(true);
                    return api.delete(`filaments/${materialId}`)
                        .then(response => {
                            setLoadingDelete(false);
                            // Atualiza o estado após remoção
                            setMaterials(materials.filter(material => material.id !== materialId));
                            Swal.fire('Removido!', 'O material foi removido com sucesso.', 'success');
                        })
                        .catch(error => {
                            setLoadingDelete(false);
                            console.error('Erro ao remover o material:', error);
                            Swal.fire('Erro!', 'Houve um problema ao remover o material.', 'error');
                        });
                }
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Filamentos</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0
            }}>
                {/* Título */}
                <Typography variant="h4" gutterBottom>
                    Filamentos
                </Typography>

                {/* Campo de busca + botões */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        gap: 2,
                        width: isMobile ? '100%' : 'auto'
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar filamento..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth={isMobile}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Buscar
                    </Button>
                    {appliedSearchTerm && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar Busca
                        </Button>
                    )}
                </Box>

                {/* Botão de venda */}
                <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateMaterial}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                    >
                        Filamento
                    </Button>
                </Box>
            </Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Marca</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Cor</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Peso (g)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Custo por KG</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Data de Criação</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(materials) && materials.length > 0 && materials.map((material) => (
                            <TableRow key={material.id}>
                                <TableCell>{formatUuid(material.id)}</TableCell>
                                <TableCell>{material.type}</TableCell>
                                <TableCell>{material.brand}</TableCell>
                                <TableCell>
                                    <ColorSwatchLabel
                                        color={material.color}
                                        label={material.color_label}
                                    />
                                </TableCell>
                                <TableCell>{material.spool_weight}</TableCell>
                                <TableCell>{formatCurrencyInCents(material.cost_per_kilogram)}</TableCell>
                                <TableCell>{formatDate(material.created_at)}</TableCell>
                                <TableCell>
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleEditMaterial(material.id)}
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDeleteMaterial(material.id)}
                                        aria-label="remover material"
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2, // Espaçamento entre o Select e a paginação
                    mt: 3, // Margem superior para afastar do conteúdo acima
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        const newLimit = event.target.value;
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
        </div>
    );
}

export default ListMaterialsPage;
